import * as THREE from 'three';
import { GameMode, GameModeContext } from './GameMode';
import { MissionHUD } from '../ui/MissionHUD';
import { EventBus } from '../core/EventBus';
import { FlyingDrone } from '../entities/FlyingDrone';
import { HeavyTank } from '../entities/HeavyTank';
import { SniperTower } from '../entities/SniperTower';
import { MegaMech } from '../entities/MegaMech';

export type ObjectiveType =
  | 'destroy'      // Kill X enemies
  | 'survive'      // Survive for X seconds
  | 'collect'      // Collect X items
  | 'reach'        // Reach a location
  | 'protect';     // Stay in area for X seconds

export interface Objective {
  type: ObjectiveType;
  description: string;
  target: number;
  current: number;
  completed: boolean;
  position?: THREE.Vector3;  // For 'reach' objectives
  radius?: number;           // For 'reach' and 'protect'
  timeLimit?: number;        // For 'survive' and 'protect'
}

export interface MissionDefinition {
  id: string;
  name: string;
  briefing: string;
  objectives: Objective[];
  timeLimit?: number;  // Optional overall time limit
  spawnEnemies?: {
    drones: number;
    tanks: number;
    snipers: number;
    boss: boolean;
  };
  rewards: {
    points: number;
    unlocks?: string[];
  };
}

export type MissionState = 'briefing' | 'active' | 'completed' | 'failed';

export class MissionMode extends GameMode {
  private hud: MissionHUD;
  private missions: MissionDefinition[] = [];
  private currentMissionIndex: number = -1;
  private state: MissionState = 'briefing';

  // Mission progress
  private objectives: Objective[] = [];
  private missionStartTime: number = 0;

  // Spawned enemies for mission
  private drones: FlyingDrone[] = [];
  private tanks: HeavyTank[] = [];
  private snipers: SniperTower[] = [];
  private boss: MegaMech | null = null;

  // Tracking
  private killCount: number = 0;

  // Objective markers
  private objectiveMarkers: THREE.Group[] = [];

  constructor(context: GameModeContext) {
    super(context);
    this.hud = new MissionHUD();
    this.loadMissions();
    this.setupEventListeners();
  }

  public getName(): string {
    return 'Missions';
  }

  private loadMissions(): void {
    this.missions = [
      {
        id: 'tutorial',
        name: 'First Blood',
        briefing: 'Welcome, pilot. Your first mission is simple: eliminate the drone patrol in the area. Show us what you can do.',
        objectives: [
          { type: 'destroy', description: 'Destroy enemy drones', target: 5, current: 0, completed: false },
        ],
        spawnEnemies: { drones: 5, tanks: 0, snipers: 0, boss: false },
        rewards: { points: 1000 },
      },
      {
        id: 'survival',
        name: 'Under Siege',
        briefing: 'Enemy forces are converging on your position. Hold out for 60 seconds until backup arrives.',
        objectives: [
          { type: 'survive', description: 'Survive the assault', target: 60, current: 0, completed: false, timeLimit: 60 },
        ],
        spawnEnemies: { drones: 8, tanks: 2, snipers: 0, boss: false },
        rewards: { points: 2000 },
      },
      {
        id: 'hunt',
        name: 'Tank Hunter',
        briefing: 'Heavy armor units have been spotted in the district. Destroy all enemy tanks. Target their rear weak points for maximum damage.',
        objectives: [
          { type: 'destroy', description: 'Destroy all tanks', target: 4, current: 0, completed: false },
        ],
        spawnEnemies: { drones: 3, tanks: 4, snipers: 0, boss: false },
        rewards: { points: 2500 },
      },
      {
        id: 'recon',
        name: 'Recon Run',
        briefing: 'Intel suggests enemy activity at multiple locations. Investigate each waypoint to gather data.',
        objectives: [
          { type: 'reach', description: 'Reach waypoint Alpha', target: 1, current: 0, completed: false, position: new THREE.Vector3(50, 0, 50), radius: 10 },
          { type: 'reach', description: 'Reach waypoint Beta', target: 1, current: 0, completed: false, position: new THREE.Vector3(-60, 0, 30), radius: 10 },
          { type: 'reach', description: 'Reach waypoint Gamma', target: 1, current: 0, completed: false, position: new THREE.Vector3(0, 0, -70), radius: 10 },
        ],
        timeLimit: 90,
        spawnEnemies: { drones: 4, tanks: 1, snipers: 2, boss: false },
        rewards: { points: 1500 },
      },
      {
        id: 'sniper_alley',
        name: 'Sniper Alley',
        briefing: 'Enemy sniper towers have locked down the area. Destroy them before they can target civilians.',
        objectives: [
          { type: 'destroy', description: 'Destroy sniper towers', target: 4, current: 0, completed: false },
        ],
        spawnEnemies: { drones: 2, tanks: 0, snipers: 4, boss: false },
        rewards: { points: 2000 },
      },
      {
        id: 'holdout',
        name: 'Last Stand',
        briefing: 'Command needs you to hold position at the central plaza. Stay within the zone while eliminating hostiles.',
        objectives: [
          { type: 'protect', description: 'Hold position', target: 45, current: 0, completed: false, position: new THREE.Vector3(0, 0, 0), radius: 20, timeLimit: 45 },
          { type: 'destroy', description: 'Eliminate hostiles', target: 10, current: 0, completed: false },
        ],
        spawnEnemies: { drones: 6, tanks: 2, snipers: 2, boss: false },
        rewards: { points: 3000 },
      },
      {
        id: 'boss_fight',
        name: 'Showdown',
        briefing: 'The enemy has deployed their ultimate weapon: a Mega Mech. This is it, pilot. Take it down.',
        objectives: [
          { type: 'destroy', description: 'Destroy the Mega Mech', target: 1, current: 0, completed: false },
        ],
        spawnEnemies: { drones: 0, tanks: 0, snipers: 0, boss: true },
        rewards: { points: 5000 },
      },
    ];

    // Load completion status from localStorage
    for (const mission of this.missions) {
      const completed = localStorage.getItem(`mission_${mission.id}_completed`);
      if (completed) {
        // Could use this to show completion status in mission select
      }
    }
  }

  private setupEventListeners(): void {
    EventBus.on('target:destroyed', (data) => {
      if (this.state !== 'active') return;

      this.killCount++;

      // Update destroy objectives
      for (const obj of this.objectives) {
        if (obj.type === 'destroy' && !obj.completed) {
          // Check if this kill counts for the objective
          const isTank = data.id.startsWith('tank');
          const isSniper = data.id.startsWith('sniper');
          const isBoss = data.id.startsWith('megamech');
          const isDrone = data.id.startsWith('drone');

          const desc = obj.description.toLowerCase();

          if (
            (desc.includes('tank') && isTank) ||
            (desc.includes('sniper') && isSniper) ||
            (desc.includes('mega mech') && isBoss) ||
            (desc.includes('drone') && isDrone) ||
            (desc.includes('hostiles') || desc.includes('enemies'))
          ) {
            obj.current++;
            if (obj.current >= obj.target) {
              obj.completed = true;
            }
          }
        }
      }

      this.updateObjectiveDisplay();
      this.checkMissionComplete();
    });

    EventBus.on('player:died', () => {
      if (this.state === 'active') {
        this.onMissionFailed('You were destroyed');
      }
    });
  }

  protected onStart(): void {
    this.hud.show();
    if (this.currentMissionIndex >= 0) {
      this.showBriefing();
    } else {
      this.selectMission(0);
    }
  }

  protected onStop(): void {
    this.hud.hide();
    this.clearMission();
  }

  public selectMission(index: number): void {
    if (index < 0 || index >= this.missions.length) return;

    this.currentMissionIndex = index;
    this.showBriefing();
  }

  private showBriefing(): void {
    this.state = 'briefing';
    const mission = this.missions[this.currentMissionIndex];
    this.hud.showBriefing(mission.name, mission.briefing, mission.objectives.map(o => o.description));
  }

  public startMission(): void {
    this.clearMission();

    const mission = this.missions[this.currentMissionIndex];
    this.state = 'active';
    this.missionStartTime = performance.now();
    this.killCount = 0;

    // Deep copy objectives
    this.objectives = mission.objectives.map(o => ({
      ...o,
      current: 0,
      completed: false,
      position: o.position?.clone(),
    }));

    // Spawn enemies
    if (mission.spawnEnemies) {
      this.spawnMissionEnemies(mission.spawnEnemies);
    }

    // Create objective markers
    this.createObjectiveMarkers();

    this.hud.hideBriefing();
    this.hud.showObjectives(this.objectives);

    if (mission.timeLimit) {
      this.hud.showTimer(mission.timeLimit);
    }
  }

  private spawnMissionEnemies(config: { drones: number; tanks: number; snipers: number; boss: boolean }): void {
    const spawnRadius = 60;

    // Spawn drones
    for (let i = 0; i < config.drones; i++) {
      const angle = (i / config.drones) * Math.PI * 2;
      const pos = new THREE.Vector3(
        Math.cos(angle) * spawnRadius * 0.8,
        5,
        Math.sin(angle) * spawnRadius * 0.8
      );

      const drone = new FlyingDrone(this.context.scene, this.context.projectilePool, {
        position: pos,
        targetRef: this.context.playerMesh,
      });
      this.drones.push(drone);
      this.context.weaponManager.registerTarget(drone.getMesh());
    }

    // Spawn tanks
    for (let i = 0; i < config.tanks; i++) {
      const angle = (i / config.tanks) * Math.PI * 2 + Math.PI / 4;
      const pos = new THREE.Vector3(
        Math.cos(angle) * spawnRadius,
        0,
        Math.sin(angle) * spawnRadius
      );

      const tank = new HeavyTank(this.context.scene, this.context.projectilePool, {
        position: pos,
        targetRef: this.context.playerMesh,
      });
      this.tanks.push(tank);
      this.context.weaponManager.registerTarget(tank.getMesh());
    }

    // Spawn snipers
    for (let i = 0; i < config.snipers; i++) {
      const angle = (i / config.snipers) * Math.PI * 2 + Math.PI / 6;
      const pos = new THREE.Vector3(
        Math.cos(angle) * spawnRadius * 1.1,
        0,
        Math.sin(angle) * spawnRadius * 1.1
      );

      const sniper = new SniperTower(this.context.scene, this.context.projectilePool, {
        position: pos,
        targetRef: this.context.playerMesh,
      });
      this.snipers.push(sniper);
      this.context.weaponManager.registerTarget(sniper.getMesh());
    }

    // Spawn boss
    if (config.boss) {
      const pos = new THREE.Vector3(80, 0, 0);
      this.boss = new MegaMech(this.context.scene, this.context.projectilePool, {
        position: pos,
        targetRef: this.context.playerMesh,
      });
      this.context.weaponManager.registerTarget(this.boss.getMesh());
    }
  }

  private createObjectiveMarkers(): void {
    for (const obj of this.objectives) {
      if (obj.position && (obj.type === 'reach' || obj.type === 'protect')) {
        const marker = this.createMarker(obj.position, obj.radius || 10, obj.type);
        this.objectiveMarkers.push(marker);
        this.context.scene.add(marker);
      }
    }
  }

  private createMarker(position: THREE.Vector3, radius: number, type: ObjectiveType): THREE.Group {
    const group = new THREE.Group();

    const color = type === 'protect' ? 0x00ff00 : 0xffff00;

    // Ground ring
    const ringGeom = new THREE.RingGeometry(radius - 0.5, radius, 32);
    const ringMat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.5,
      side: THREE.DoubleSide,
    });
    const ring = new THREE.Mesh(ringGeom, ringMat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.1;
    group.add(ring);

    // Vertical beacon
    const beaconGeom = new THREE.CylinderGeometry(0.3, 0.3, 20, 8);
    const beaconMat = new THREE.MeshBasicMaterial({
      color,
      transparent: true,
      opacity: 0.3,
    });
    const beacon = new THREE.Mesh(beaconGeom, beaconMat);
    beacon.position.y = 10;
    group.add(beacon);

    // Top sphere
    const sphereGeom = new THREE.SphereGeometry(1, 16, 16);
    const sphereMat = new THREE.MeshStandardMaterial({
      color,
      emissive: color,
      emissiveIntensity: 1,
    });
    const sphere = new THREE.Mesh(sphereGeom, sphereMat);
    sphere.position.y = 20;
    group.add(sphere);

    group.position.copy(position);
    return group;
  }

  private clearMission(): void {
    // Clear enemies
    for (const drone of this.drones) {
      const mesh = drone.getMesh();
      if (mesh.parent) mesh.parent.remove(mesh);
    }
    this.drones = [];

    for (const tank of this.tanks) {
      const mesh = tank.getMesh();
      if (mesh.parent) mesh.parent.remove(mesh);
    }
    this.tanks = [];

    for (const sniper of this.snipers) {
      const mesh = sniper.getMesh();
      if (mesh.parent) mesh.parent.remove(mesh);
    }
    this.snipers = [];

    if (this.boss) {
      const mesh = this.boss.getMesh();
      if (mesh.parent) mesh.parent.remove(mesh);
      this.boss = null;
    }

    // Clear markers
    for (const marker of this.objectiveMarkers) {
      this.context.scene.remove(marker);
    }
    this.objectiveMarkers = [];

    this.objectives = [];
  }

  public update(dt: number): void {
    if (!this.isActive || this.state !== 'active') return;

    const mission = this.missions[this.currentMissionIndex];

    // Update enemies
    for (const drone of this.drones) {
      drone.update(dt);
    }
    for (const tank of this.tanks) {
      tank.update(dt);
    }
    for (const sniper of this.snipers) {
      sniper.update(dt);
    }
    if (this.boss?.isAlive()) {
      this.boss.update(dt);
    }

    // Cleanup dead enemies
    this.drones = this.drones.filter(d => d.isAlive());
    this.tanks = this.tanks.filter(t => t.isAlive());
    this.snipers = this.snipers.filter(s => s.isAlive());

    // Update time-based objectives
    const playerPos = this.context.getPlayerPosition();

    for (const obj of this.objectives) {
      if (obj.completed) continue;

      if (obj.type === 'survive') {
        obj.current += dt;
        if (obj.current >= obj.target) {
          obj.completed = true;
        }
      } else if (obj.type === 'protect' && obj.position) {
        const distance = playerPos.distanceTo(obj.position);
        if (distance <= (obj.radius || 20)) {
          obj.current += dt;
          if (obj.current >= obj.target) {
            obj.completed = true;
          }
        }
      } else if (obj.type === 'reach' && obj.position) {
        const distance = playerPos.distanceTo(obj.position);
        if (distance <= (obj.radius || 10)) {
          obj.current = 1;
          obj.completed = true;
        }
      }
    }

    // Update timer
    if (mission.timeLimit) {
      const elapsed = (performance.now() - this.missionStartTime) / 1000;
      const remaining = mission.timeLimit - elapsed;

      if (remaining <= 0) {
        this.onMissionFailed('Time ran out');
        return;
      }

      this.hud.updateTimer(remaining);
    }

    this.updateObjectiveDisplay();
    this.checkMissionComplete();
  }

  private updateObjectiveDisplay(): void {
    this.hud.updateObjectives(this.objectives);
  }

  private checkMissionComplete(): void {
    const allComplete = this.objectives.every(o => o.completed);

    if (allComplete) {
      this.onMissionComplete();
    }
  }

  private onMissionComplete(): void {
    this.state = 'completed';
    const mission = this.missions[this.currentMissionIndex];
    const time = (performance.now() - this.missionStartTime) / 1000;

    // Save completion
    localStorage.setItem(`mission_${mission.id}_completed`, 'true');

    // Award points
    EventBus.emit('score:added', {
      points: mission.rewards.points,
      reason: 'mission_complete',
    });

    this.hud.showMissionComplete(mission.name, time, mission.rewards.points, this.killCount);
  }

  private onMissionFailed(reason: string): void {
    this.state = 'failed';
    const mission = this.missions[this.currentMissionIndex];
    this.hud.showMissionFailed(mission.name, reason);
  }

  public restartMission(): void {
    this.hud.hideResults();
    this.startMission();
  }

  public nextMission(): void {
    this.hud.hideResults();
    const nextIndex = this.currentMissionIndex + 1;
    if (nextIndex < this.missions.length) {
      this.selectMission(nextIndex);
    } else {
      // All missions complete
      this.hud.showAllComplete();
    }
  }

  public getMissions(): MissionDefinition[] {
    return this.missions;
  }

  public getCurrentMission(): MissionDefinition | null {
    return this.currentMissionIndex >= 0 ? this.missions[this.currentMissionIndex] : null;
  }

  public getState(): MissionState {
    return this.state;
  }

  public getEnemyPositions(): THREE.Vector3[] {
    const positions: THREE.Vector3[] = [];
    for (const drone of this.drones) {
      if (drone.isAlive()) positions.push(drone.getPosition());
    }
    for (const tank of this.tanks) {
      if (tank.isAlive()) positions.push(tank.getPosition());
    }
    for (const sniper of this.snipers) {
      if (sniper.isAlive()) positions.push(sniper.getPosition());
    }
    if (this.boss?.isAlive()) {
      positions.push(this.boss.getPosition());
    }
    return positions;
  }
}
